import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/browser'
import { isProduction } from '@/utils/appUtilities'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'
import isMobile from 'is-mobile'
import { setUTMCookies } from './cookie'
import posthog from 'posthog-js'
import { logException, logMessage } from './sentry'
import { analytics } from './segment'

if (!process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN) {
  const errorMessage = 'Cannot initialize Mixpanel without a token'
  Sentry.captureMessage(errorMessage)
  console.error(errorMessage)
}

mixpanel.init(process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN as string, {
  debug: !isProduction,
  batch_requests: false,
})

export type AppSurface = 'mobile-native' | 'mobile-web' | 'web'

/**
 * Which surface the user is on: the native app (this web app inside the React
 * Native webview), a mobile browser, or a desktop browser.
 */
export function appSurface(): AppSurface {
  if (isNativeMobile()) return 'mobile-native'
  return isMobile() ? 'mobile-web' : 'web'
}

/**
 * Stamp the surface on every PostHog event as a super property.
 *
 * `trackPageview` already reports the same value, but it does so under `$set`,
 * which writes it to the *person* profile — last write wins, so a user who ever
 * opens the web app overwrites their app sessions and vice versa. Nothing
 * downstream can split a funnel by surface from that. Registering makes it an
 * *event* property instead, so each event carries the surface it happened on.
 *
 * `window.appVersion` is injected by the mobile app before content loads
 * (mobile `injectVersion.js`), so it is already set by the time this runs; it
 * is undefined on web.
 */
function registerSurfaceProperties() {
  posthog.register({
    app_surface: appSurface(),
    app_version: window.appVersion ?? null,
  })
}

if (
  process.env.REACT_APP_PUBLIC_POSTHOG_KEY &&
  process.env.REACT_APP_PUBLIC_POSTHOG_HOST
) {
  posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY ?? '', {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'always',
    disable_web_experiments: false,
    capture_pageview: false,
  })
  registerSurfaceProperties()
} else {
  const errorMessage = 'Cannot initialize Posthog without a key and host'
  logMessage(errorMessage)
  console.error(errorMessage)
}

export function identify(userId: string, email?: string, name?: string) {
  if (!isProduction) {
    console.log(`Identifying user: ${userId}`)
    return
  }
  if (!posthog._isIdentified()) {
    posthog.identify(userId, { email, name })
  }
  mixpanel.identify(userId)

  // Identify user with Segment
  analytics.identify(userId, {
    email,
    name,
  })
}

function _resetIdentity() {
  if (!isProduction) {
    console.log(`Resetting identity`)
    return
  }
  posthog.reset()
  // reset() clears super properties along with the identity — put the surface back.
  registerSurfaceProperties()
  mixpanel.reset()

  // Reset identity with Segment
  analytics.reset()
}

export function resetIdentity() {
  try {
    _resetIdentity()
  } catch (e) {
    logException(e)
  }
}

export function track(
  event: string,
  params: {
    [key: string]: any
  } | null = null,
) {
  if (!isProduction) {
    console.log(`Tracking event: ${event}`, params)
    return
  }
  posthog.capture(event, { eventParams: params })
  mixpanel.track(event, { eventParams: params })

  // Track event with Segment
  analytics.track(event, params || {})
}

export function trackPosthog(
  event: string,
  params: { [key: string]: any } | null = null,
) {
  if (!isProduction) {
    console.log(`Tracking posthog event: ${event}`, params)
    return
  }

  posthog.capture(event, { eventParams: params })
}

export function trackPageview() {
  const properties = {
    platform: appSurface(),
    source: isMobile() ? 'mobile' : 'web',
  }

  // Extract UTM parameters for more accessible analytics
  const urlParams = new URLSearchParams(window.location.search)
  const utmProperties: Record<string, string | null> = {}

  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ]
  utmParams.forEach((param) => {
    const value = urlParams.get(param)
    if (value) {
      utmProperties[param] = value
    }
  })

  if (!isProduction) {
    console.log(
      `Tracking Pageview: ${document.location} with properties:`,
      properties,
    )
    return
  }
  posthog.capture('$pageview', {
    $set: {
      ...properties,
    },
  })
  mixpanel.track_pageview(properties)

  // Track page view with Segment
  analytics.page({
    path: window.location.pathname,
    url: window.location.href,
    title: document.title,
    search: window.location.search,
    properties: {
      ...properties,
      ...utmProperties,
    },
  })
}

/**
 * This is a workaround to track pageviews in a single page application.
 */
history.pushState = ((f) =>
  function pushState(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const ret = f.apply(this, args)
    window.dispatchEvent(new Event('locationchange'))
    return ret
  })(history.pushState)

history.replaceState = ((f) =>
  function replaceState(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const ret = f.apply(this, args)
    window.dispatchEvent(new Event('locationchange'))
    return ret
  })(history.replaceState)

window.addEventListener('popstate', () => {
  window.dispatchEvent(new Event('locationchange'))
})

window.addEventListener('locationchange', () => {
  console.log(`location: ${document.location}`)
  setUTMCookies(location.search)
  trackPageview()
})
window.addEventListener('load', () => {
  console.log(`location: ${document.location}`)
  setUTMCookies(location.search)
  trackPageview()
})
